/**
 * API Utility Functions
 *
 * WordPress REST API wrapper with authentication.
 *
 * @package MultilingualPressZone
 * @since 1.0.0
 */

import logger from './logger.js';

/**
 * API Client Class
 */
class ApiClient {
    constructor() {
        this.baseUrl = window.multilingualPressZone?.apiUrl || '/wp-json/multilingual-press-zone/v1';
        this.nonce = window.multilingualPressZone?.nonce || '';
        this.restRoot = this.deriveRestRoot(this.baseUrl);
    }

    /**
     * Derive the REST API root (ending with `/wp-json/`) from a base REST URL.
     *
     * @param {string} baseUrl
     * @return {string}
     */
    deriveRestRoot(baseUrl) {
        try {
            const url = new URL(baseUrl, window.location.href);
            const marker = '/wp-json/';
            const idx = url.href.indexOf(marker);
            if (idx !== -1) {
                return url.href.slice(0, idx + marker.length);
            }
        } catch (e) {
            // ignore
        }

        return '/wp-json/';
    }

    /**
     * Resolve an endpoint to an absolute URL.
     *
     * Supports:
     * - Relative endpoints against `this.baseUrl` (e.g. `/languages`)
     * - Full REST routes (e.g. `/mpz/v1/migration/preflight`)
     * - Absolute URLs
     *
     * @param {string} endpoint
     * @return {string}
     */
    resolveUrl(endpoint) {
        if (typeof endpoint !== 'string') {
            return `${this.baseUrl}`;
        }

        // Absolute URL
        if (/^https?:\/\//i.test(endpoint)) {
            return endpoint;
        }

        // Absolute REST path (namespace-based), e.g. `/mpz/v1/...`
        if (endpoint.startsWith('/mpz/') || endpoint.startsWith('/multilingual-press-zone/')) {
            return `${this.restRoot}${endpoint.replace(/^\//, '')}`;
        }

        // Already includes `/wp-json/`
        if (endpoint.startsWith('/wp-json/')) {
            const base = this.restRoot.replace(/\/$/, '');
            return `${base}${endpoint}`;
        }

        // Default: relative to plugin API base
        const base = String(this.baseUrl || '').replace(/\/+$/, '');
        const path = String(endpoint || '').replace(/^\/+/, '');
        if (!base) {
            return `/${path}`;
        }
        if (!path) {
            return base;
        }

        // Final normalization: avoid accidental double slashes in paths
        // (e.g. base ends with '/', endpoint starts with '/').
        return `${base}/${path}`.replace(/(^|[^:])\/{2,}/g, '$1/');
    }

    /**
     * Make an API request
     *
     * @param {string} endpoint - API endpoint
     * @param {Object} options - Fetch options
     * @return {Promise<any>} Response data
     */
    async request(endpoint, options = {}) {
        const url = this.resolveUrl(endpoint);

        const defaultOptions = {
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': this.nonce
            },
            credentials: 'same-origin'
        };

        const finalOptions = {
            ...defaultOptions,
            ...options,
            headers: {
                ...defaultOptions.headers,
                ...options.headers
            }
        };

        logger.debug(`API Request: ${options.method || 'GET'} ${url}`);

        try {
            const response = await fetch(url, finalOptions);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            logger.debug('API Response:', data);
            return data;
        } catch (error) {
            logger.error('API Error:', error);
            throw error;
        }
    }

    /**
     * GET request
     *
     * @param {string} endpoint - API endpoint
     * @param {Object} params - Query parameters
     * @return {Promise<any>} Response data
     */
    async get(endpoint, params = {}) {
        const queryString = new URLSearchParams(params).toString();
        const url = queryString ? `${endpoint}?${queryString}` : endpoint;

        return this.request(url, {
            method: 'GET'
        });
    }

    /**
     * POST request
     *
     * @param {string} endpoint - API endpoint
     * @param {Object} data - Request body
     * @return {Promise<any>} Response data
     */
    async post(endpoint, data = {}) {
        return this.request(endpoint, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    /**
     * PUT request
     *
     * @param {string} endpoint - API endpoint
     * @param {Object} data - Request body
     * @return {Promise<any>} Response data
     */
    async put(endpoint, data = {}) {
        return this.request(endpoint, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    }

    /**
     * PATCH request
     *
     * @param {string} endpoint - API endpoint
     * @param {Object} data - Request body
     * @return {Promise<any>} Response data
     */
    async patch(endpoint, data = {}) {
        return this.request(endpoint, {
            method: 'PATCH',
            body: JSON.stringify(data)
        });
    }

    /**
     * DELETE request
     *
     * @param {string} endpoint - API endpoint
     * @return {Promise<any>} Response data
     */
    async delete(endpoint) {
        return this.request(endpoint, {
            method: 'DELETE'
        });
    }

    /**
     * Request to api.press.zone backend
     *
     * @param {string} endpoint - API endpoint
     * @param {Object} options - Fetch options
     * @return {Promise<any>} Response data
     */
    async requestExternal(endpoint, options = {}) {
        const apiKey = window.multilingualPressZone?.apiKey || '';
        const url = `https://api.press.zone${endpoint}`;

        const defaultOptions = {
            headers: {
                'Content-Type': 'application/json',
                'X-API-Key': apiKey
            }
        };

        const finalOptions = {
            ...defaultOptions,
            ...options,
            headers: {
                ...defaultOptions.headers,
                ...options.headers
            }
        };

        logger.debug(`External API Request: ${options.method || 'GET'} ${url}`);

        try {
            const response = await fetch(url, finalOptions);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            logger.debug('External API Response:', data);
            return data;
        } catch (error) {
            logger.error('External API Error:', error);
            throw error;
        }
    }
}

// Export singleton instance
export default new ApiClient();
